/**
 * Feb 2026 · QC Approval Queue.
 *
 * Warehouse manager's console for every Receipt txn / Lot currently
 * "Awaiting Inspection" or "Hold". One-click Approve / Reject / Hold.
 */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { CheckCircleIcon, XCircleIcon, PauseCircleIcon, ArrowsClockwiseIcon } from "@phosphor-icons/react";

const badgeCls = (q) => ({
  "Passed": "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
  "Awaiting Inspection": "bg-amber-500/15 text-amber-300 border-amber-500/30",
  "Rejected": "bg-red-500/15 text-red-300 border-red-500/30",
  "Hold": "bg-sky-500/15 text-sky-300 border-sky-500/30",
}[q] || "bg-zinc-500/15 text-zinc-300 border-zinc-500/30");

export default function QCApproval() {
  const [data, setData] = useState({ lots: [], receipt_txns: [], counts: { lots: 0, txns: 0 } });
  const [rms, setRms] = useState({});
  const [godowns, setGodowns] = useState({});
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const [{ data: pending }, { data: rmList }, { data: gs }] = await Promise.all([
        api.get("/inventory/qc-pending"),
        api.get("/raw-materials/"),
        api.get("/godowns/"),
      ]);
      setData(pending);
      setRms(Object.fromEntries((rmList || []).map(r => [r.id, r])));
      setGodowns(Object.fromEntries((gs || []).map(g => [g.id, g])));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load QC queue");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const decide = async ({ txn_id, lot_id }, status) => {
    const key = txn_id || lot_id;
    setBusy(key);
    try {
      const notes = status === "Rejected" ? window.prompt("Rejection reason?") || "" : "";
      if (status === "Rejected" && !notes) { setBusy(""); return; }
      await api.post("/inventory/qc-update", { txn_id: txn_id || "", lot_id: lot_id || "", quality_status: status, quality_notes: notes });
      toast.success(`Marked ${status}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    } finally { setBusy(""); }
  };

  const Row = ({ item, kind }) => {
    const rm = rms[item.raw_material_id] || {};
    const g = godowns[item.godown_id] || {};
    const key = kind === "txn" ? item.id : item.id;
    // Feb 2026 · GRN reference — for `lot` rows the `lot_no` IS the GRN
    // document number (or OPB-… for opening stock); for `txn` rows the
    // `reference` field carries the parent GRN/OPB doc. Fall back to the
    // dedicated `grn_no` column when present.
    const grnNo = item.grn_no || item.lot_no || item.reference || "—";
    return (
      <tr className="border-t border-[var(--border)] hover:bg-[var(--bg-2)]/30" data-testid={`qc-${kind}-${key}`}>
        <td className="px-3 py-2 text-xs uppercase tracking-widest text-zinc-500 font-mono">{kind === "txn" ? "Receipt" : "Lot"}</td>
        <td className="px-3 py-2 text-xs font-mono text-[var(--accent)]" data-testid={`qc-${kind}-${key}-grn`}>{grnNo}</td>
        <td className="px-3 py-2 text-xs"><span className="font-mono text-[var(--accent)]">{rm.code}</span><div className="text-[10px] text-zinc-500">{rm.name}</div></td>
        <td className="px-3 py-2 text-xs font-mono text-zinc-300" data-testid={`qc-${kind}-${key}-batch`}>{item.batch_no || <span className="text-zinc-600">—</span>}</td>
        <td className="px-3 py-2 text-xs">{g.name || "—"}<div className="text-[10px] text-zinc-500">{g.department || ""}</div></td>
        <td className="px-3 py-2 text-right font-mono tabular-nums text-sm">{Number(item.quantity || item.qty_received || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })} {rm.uom}</td>
        <td className="px-3 py-2 text-xs text-zinc-400">{(item.txn_date || item.received_date || "").slice(0, 10)}</td>
        <td className="px-3 py-2"><span className={`inline-flex items-center px-2 py-0.5 rounded-sm border text-[10px] font-mono uppercase tracking-widest ${badgeCls(item.quality_status)}`}>{item.quality_status}</span></td>
        <td className="px-3 py-2 text-right">
          {["Passed", "Hold", "Rejected"].map(s => (
            <button
              key={s}
              data-testid={`qc-${kind}-${key}-${s.toLowerCase()}`}
              disabled={busy === key}
              onClick={() => decide(kind === "txn" ? { txn_id: item.id } : { lot_id: item.id }, s)}
              title={s}
              className={`mx-0.5 h-7 px-2 rounded-sm text-[10px] font-mono uppercase tracking-widest border transition ${
                s === "Passed" ? "border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10" :
                s === "Hold"   ? "border-sky-500/40 text-sky-300 hover:bg-sky-500/10" :
                                 "border-red-500/40 text-red-300 hover:bg-red-500/10"
              } disabled:opacity-40`}
            >
              {s === "Passed" ? <CheckCircleIcon size={11} className="inline" weight="bold" /> : s === "Hold" ? <PauseCircleIcon size={11} className="inline" weight="bold" /> : <XCircleIcon size={11} className="inline" weight="bold" />}
              <span className="ml-1">{s}</span>
            </button>
          ))}
        </td>
      </tr>
    );
  };

  const all = [
    ...data.receipt_txns.map(t => ({ ...t, __kind: "txn" })),
    ...data.lots.map(l => ({ ...l, __kind: "lot" })),
  ];

  return (
    <div className="p-6 space-y-4" data-testid="qc-approval-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="overline mb-1">Inventory · Gatekeeping</div>
          <h1 className="font-display text-3xl font-black tracking-tight">QC Approval Queue</h1>
          <p className="text-xs text-zinc-500 mt-1">Every incoming Receipt / Lot awaiting inspection · warehouse manager clears them into Passed stock before production issue</p>
        </div>
        <button data-testid="qc-refresh" onClick={load} className="h-9 px-4 border border-[var(--border)] hover:bg-[var(--bg-2)] text-sm font-semibold rounded-sm flex items-center gap-2">
          <ArrowsClockwiseIcon size={13} weight="bold" />Refresh
        </button>
      </div>

      <div className="tactical-card p-4">
        <div className="border border-[var(--border)] rounded-sm overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[var(--bg-2)]/50 text-zinc-500 uppercase tracking-widest text-[10px]">
              <tr>
                <th className="px-3 py-2 text-left">Type</th>
                <th className="px-3 py-2 text-left">GRN No</th>
                <th className="px-3 py-2 text-left">Raw Material</th>
                <th className="px-3 py-2 text-left">Batch No</th>
                <th className="px-3 py-2 text-left">Godown</th>
                <th className="px-3 py-2 text-right">Qty</th>
                <th className="px-3 py-2 text-left">Date</th>
                <th className="px-3 py-2 text-left">Status</th>
                <th className="px-3 py-2 text-right">Action</th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={9} className="text-center py-8 text-zinc-500 text-xs">Loading…</td></tr>}
              {!loading && all.length === 0 && <tr><td colSpan={9} className="text-center py-8 text-emerald-300 text-xs">QC queue clear — everything is Passed</td></tr>}
              {all.map((it, i) => <Row key={it.id + i} item={it} kind={it.__kind} />)}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
